import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, Search, FileText, User, X, 
  Mail, Printer, AlertCircle, Check, ChevronRight, 
  ChevronLeft, RotateCcw, Edit, MessageSquare, Phone
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi } from '../services/authApi';

// Print styles for professional patient handouts
const printStyles = `
  @media screen {
    .print-only {
      display: none !important;
    }
  }
  
  @media print {
    @page {
      margin: 0.5in;
      size: letter portrait;
    }
    
    /* Hide everything except print content */
    body > *:not(.print-only) {
      display: none !important;
    }
    
    /* Show only print content */
    .print-only {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
    }
    
    /* Ensure colors and backgrounds print */
    * {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    /* Page breaks - avoid breaking inside important elements */
    h1, h2, h3, h4, h5, h6 {
      page-break-after: avoid !important;
      page-break-inside: avoid !important;
    }
    
    img {
      page-break-inside: avoid !important;
    }
    
    /* Remove any extra blank pages */
    .print-only > div:last-child {
      page-break-after: avoid !important;
    }
  }
`;

const AssignMedCardPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { practice, user } = useAuth();

  // Disclaimer state
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [signature, setSignature] = useState('');
  const [checkingDisclaimer, setCheckingDisclaimer] = useState(true);

  // Main state
  const [currentStep, setCurrentStep] = useState(1);
  const [patients, setPatients] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [medCards, setMedCards] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDentist, setSelectedDentist] = useState(null);
  const [selectedMedCards, setSelectedMedCards] = useState([]);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [dentistSearchTerm, setDentistSearchTerm] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [filteredDentists, setFilteredDentists] = useState([]);
  const [assignedCardIds, setAssignedCardIds] = useState([]); // Track which cards were assigned
  
  // Edit state - stores temporary edits (session-based, not saved to library)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [tempEditedContent, setTempEditedContent] = useState({}); // Temporary edits for this session only
  
  // Delivery state
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);

  useEffect(() => {
    checkDisclaimerStatus();
    loadData();
  }, []);

  useEffect(() => {
    const searchLower = patientSearchTerm.toLowerCase();
    if (!searchLower) {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter(p => 
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchLower) ||
        (p.email || "").toLowerCase().includes(searchLower)
      );
      setFilteredPatients(filtered);
    }
  }, [patientSearchTerm, patients]);

  useEffect(() => {
    const searchLower = dentistSearchTerm.toLowerCase();
    if (!searchLower) {
      setFilteredDentists(dentists);
    } else {
      const filtered = dentists.filter(d => 
        `${d.firstName} ${d.lastName}`.toLowerCase().includes(searchLower) ||
        (d.email || "").toLowerCase().includes(searchLower)
      );
      setFilteredDentists(filtered);
    }
  }, [dentistSearchTerm, dentists]);

  const checkDisclaimerStatus = async () => {
    try {
      const data = await practiceApi.getMedCardDisclaimerStatus();
      if (data.success && data.accepted) {
        setDisclaimerChecked(true);
      } else {
        setShowDisclaimer(true);
      }
    } catch (error) {
      console.error('Error checking disclaimer:', error);
      setShowDisclaimer(true);
    } finally {
      setCheckingDisclaimer(false);
    }
  };

  const handleAcceptDisclaimer = async () => {
    if (!signature.trim()) {
      toast({
        title: "Signature Required",
        description: "Please provide your signature",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      const data = await practiceApi.acceptMedCardDisclaimer(signature.trim());

      if (data.success) {
        setDisclaimerChecked(true);
        setShowDisclaimer(false);
        toast({
          title: "Disclaimer Accepted",
          description: "You can now assign Med Cards",
        });
      }
    } catch (error) {
      console.error('Error accepting disclaimer:', error);
      toast({
        title: "Error",
        description: "Failed to accept disclaimer",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      console.log('🔄 Loading data for Assign Med Card...');
      const [patientsData, dentistsData, medCardsData] = await Promise.all([
        practiceApi.getPatients(),
        practiceApi.getDentists(),
        practiceApi.getMedCards()
      ]);

      console.log('📊 Data loaded:', { 
        patients: patientsData?.data?.length || 0, 
        dentists: dentistsData?.data?.length || 0, 
        medCards: medCardsData?.data?.length || 0 
      });

      if (patientsData.success) setPatients(patientsData.data);
      if (dentistsData.success) {
        console.log('👨‍⚕️ Setting dentists:', dentistsData.data);
        setDentists(dentistsData.data);
      }
      if (medCardsData.success) setMedCards(medCardsData.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive"
      });
    }
  };

  const toggleMedCard = (cardId) => {
    setSelectedMedCards(prev => {
      if (prev.includes(cardId)) {
        // Remove card and its temporary edit
        const newEdits = { ...tempEditedContent };
        delete newEdits[cardId];
        setTempEditedContent(newEdits);
        return prev.filter(id => id !== cardId);
      } else {
        return [...prev, cardId];
      }
    });
  };

  const handleOpenEditModal = (card) => {
    setEditingCard(card);
    // Initialize with existing temp edit or original content
    const currentContent = tempEditedContent[card.id] || card.customContent || card.baseContent || '';
    setShowEditModal(true);
  };

  const handleSaveEdit = () => {
    if (!editingCard) return;
    
    const textarea = document.getElementById('edit-textarea');
    if (textarea) {
      setTempEditedContent(prev => ({
        ...prev,
        [editingCard.id]: textarea.value
      }));
      toast({
        title: "Saved",
        description: "Temporary changes saved for this assignment (not saved to library)",
      });
    }
    setShowEditModal(false);
    setEditingCard(null);
  };

  const handleResetToOriginal = () => {
    if (!editingCard) return;
    
    const textarea = document.getElementById('edit-textarea');
    if (textarea) {
      textarea.value = editingCard.customContent || editingCard.baseContent || '';
      toast({
        title: "Reset",
        description: "Content reset to original",
      });
    }
  };

  const handleAssignCards = async () => {
    if (!selectedPatient || !selectedDentist || selectedMedCards.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please select patient, dentist, and at least one Med Card",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);

      // Assign Med Cards (without saving temporary edits to library)
      const data = await practiceApi.assignMedCards({
        patientId: selectedPatient.id,
        patientName: `${selectedPatient.firstName} ${selectedPatient.lastName}`,
        patientEmail: selectedPatient.email,
        medCardIds: selectedMedCards,
        dentistId: selectedDentist.id,
        dentistName: `${selectedDentist.firstName} ${selectedDentist.lastName}`,
        notes,
        deliveryMethod: 'none' // We'll handle delivery in step 4
      });

      if (data.success) {
        setAssignedCardIds(selectedMedCards);
        setCurrentStep(4);
        toast({
          title: "Success",
          description: `${selectedMedCards.length} Med Card(s) ready for delivery`,
        });
      }
    } catch (error) {
      console.error('Error assigning Med Cards:', error);
      toast({
        title: "Error",
        description: "Failed to assign Med Cards",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // Create print content dynamically - matching Med Card Library format
    const createPrintContent = () => {
      let html = '';
      
      assignedCardIds.forEach((cardId, index) => {
        const card = medCards.find(c => c.id === cardId);
        if (!card) {
          console.error('Card not found for printing:', cardId);
          return;
        }
        
        const rawContent = getCardContent(cardId);
        console.log('📄 Printing card:', card.name);
        
        // Parse content into sections (same logic as Med Card Library)
        const lines = rawContent.split('\n');
        const sections = [];
        let currentSection = { title: '', content: [] };
        
        lines.forEach((line) => {
          const trimmed = line.trim();
          
          // Detect section headers
          if (trimmed.match(/^(Why this medicine|How much to take|How to take it|Important reminders|Common side effects|Call your dentist|Provider notice)/i)) {
            if (currentSection.title || currentSection.content.length > 0) {
              sections.push({...currentSection});
            }
            currentSection = { title: trimmed, content: [] };
          } else if (trimmed) {
            currentSection.content.push(trimmed);
          }
        });
        
        if (currentSection.title || currentSection.content.length > 0) {
          sections.push(currentSection);
        }
        
        // Filter out Provider Notice sections
        const patientSections = sections.filter(section => 
          !section.title.toLowerCase().includes('provider notice')
        );
        
        // Create sections HTML with same formatting as Med Card Library
        const sectionsHtml = patientSections.map((section) => {
          const title = section.title.toLowerCase();
          let bgColor = '#f9fafb';
          let borderColor = '#e5e7eb';
          let iconHtml = '📋';
          
          // Color code by section type (same as view)
          if (title.includes('important') || title.includes('do not')) {
            bgColor = '#fefce8';
            borderColor = '#fde047';
            iconHtml = '⚠️';
          } else if (title.includes('call') || title.includes('seek help')) {
            bgColor = '#fef2f2';
            borderColor = '#fca5a5';
            iconHtml = '🚨';
          } else if (title.includes('side effects')) {
            bgColor = '#fff7ed';
            borderColor = '#fed7aa';
            iconHtml = '⚠️';
          } else if (title.includes('how much')) {
            iconHtml = '💊';
          } else if (title.includes('why')) {
            iconHtml = '📋';
          }
          
          const contentHtml = section.content.map(line => {
            if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
              return `<p style="margin: 8px 0; display: flex; align-items: start;"><span style="color: #2563eb; font-weight: bold; margin-right: 8px;">•</span><span style="flex: 1;">${line.replace(/^[•\-*]\s*/, '')}</span></p>`;
            } else {
              return `<p style="margin: 8px 0;">${line}</p>`;
            }
          }).join('');
          
          return `
            <div style="background: ${bgColor}; border-left: 4px solid ${borderColor}; border-radius: 0 8px 8px 0; padding: 20px; margin-bottom: 16px;">
              <div style="display: flex; align-items: start; gap: 12px;">
                <span style="font-size: 20px;">${iconHtml}</span>
                <div style="flex: 1;">
                  ${section.title ? `<h3 style="font-weight: bold; font-size: 18px; margin: 0 0 12px 0; color: #1f2937;">${section.title}</h3>` : ''}
                  <div style="color: #374151; line-height: 1.6;">
                    ${contentHtml}
                  </div>
                </div>
              </div>
            </div>
          `;
        }).join('');
        
        // Only add page-break-after if NOT the last card
        const pageBreak = index < assignedCardIds.length - 1 ? 'page-break-after: always;' : '';
        
        html += `
          <div style="${pageBreak} font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
            <!-- Practice Header -->
            <div style="text-align: center; margin-bottom: 20px; padding: 20px; border: 2px solid #e5e7eb; border-radius: 8px;">
              ${practice?.logoUrl ? `<img src="${practice.logoUrl}" alt="Practice Logo" style="height: 60px; margin-bottom: 15px;" />` : ''}
              <h1 style="margin: 0; font-size: 20px; color: #1f2937;">${practice?.name || 'Dental Practice'}</h1>
              ${practice?.address ? `<p style="margin: 5px 0; color: #6b7280;">${practice.address.street || ''}, ${practice.address.city || ''}, ${practice.address.state || ''} ${practice.address.zipCode || ''}</p>` : ''}
              ${practice?.phone ? `<p style="margin: 5px 0; font-weight: bold;">Phone: ${practice.phone}</p>` : ''}
            </div>
            
            <!-- Med Card Header -->
            <div style="background: linear-gradient(to right, #2563eb, #1d4ed8); color: white; padding: 20px; margin-bottom: 20px; border-radius: 8px; display: flex; align-items: center; justify-content: space-between;">
              <div>
                <h2 style="margin: 0 0 4px 0; font-size: 24px; font-weight: bold;">${card.name}</h2>
                <p style="margin: 0; opacity: 0.9; font-size: 14px;">${card.category || ''}</p>
              </div>
              <div style="background: rgba(255,255,255,0.2); padding: 8px; border-radius: 8px;">
                <span style="font-size: 32px;">📋</span>
              </div>
            </div>
            
            <!-- Patient Info -->
            <div style="background-color: #eff6ff; padding: 15px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid #2563eb;">
              <p style="margin: 5px 0; font-size: 14px;"><strong>Patient:</strong> ${selectedPatient?.firstName} ${selectedPatient?.lastName}</p>
              <p style="margin: 5px 0; font-size: 14px;"><strong>Dentist:</strong> ${selectedDentist?.firstName} ${selectedDentist?.lastName}</p>
              <p style="margin: 5px 0; font-size: 14px;"><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
            </div>
            
            <!-- Content Sections -->
            <div style="margin-bottom: 20px;">
              ${sectionsHtml}
            </div>
          </div>
        `;
      });
      
      return html;
    };
    
    // Remove existing print area
    const existingPrintArea = document.querySelector('.print-only');
    if (existingPrintArea) {
      existingPrintArea.remove();
    }
    
    // Create new print area
    const printArea = document.createElement('div');
    printArea.className = 'print-only';
    printArea.innerHTML = createPrintContent();
    document.body.appendChild(printArea);
    
    // Print immediately
    window.print();
    toast({
      title: "Printing",
      description: "Med Cards sent to printer",
    });
  };

  const handleEmail = async () => {
    try {
      setLoading(true);
      
      // Email each selected Med Card with temporary edits
      for (const cardId of assignedCardIds) {
        const card = medCards.find(c => c.id === cardId);
        if (!card) continue;
        
        const contentToEmail = tempEditedContent[cardId] || card.customContent || card.baseContent || '';
        
        await practiceApi.emailMedCard(
          cardId,
          selectedPatient.email,
          `${selectedPatient.firstName} ${selectedPatient.lastName}`,
          contentToEmail
        );
      }

      toast({
        title: "Email Sent",
        description: `Med Card(s) emailed to ${selectedPatient.email}`,
      });
    } catch (error) {
      console.error('Error emailing Med Cards:', error);
      toast({
        title: "Error",
        description: "Failed to email Med Cards",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBoth = async () => {
    await handleEmail();
    handlePrint();
  };

  const handleSMS = async () => {
    if (!selectedPatient || assignedCardIds.length === 0) {
      toast({
        title: "Error",
        description: "No patient or med cards selected",
        variant: "destructive"
      });
      return;
    }

    const phoneNumber = selectedPatient.cellPhone || selectedPatient.phone;
    if (!phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "This patient doesn't have a phone number on file. Please add a phone number to send text messages.",
        variant: "destructive"
      });
      return;
    }

    try {
      setSendingSMS(true);
      const patientName = `${selectedPatient.firstName} ${selectedPatient.lastName}`;
      const token = localStorage.getItem('dentalToken');
      
      // Send SMS for each assigned med card
      let successCount = 0;
      for (const cardId of assignedCardIds) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/send-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            patient_id: selectedPatient.id,
            document_type: 'medcard',
            document_id: cardId,
            phone_number: phoneNumber
          })
        });
        
        const data = await response.json();
        if (data.success) {
          successCount++;
        }
      }

      if (successCount > 0) {
        toast({
          title: "Text Sent",
          description: `${successCount} Med Card(s) texted to ${patientName}`,
        });
      } else {
        throw new Error('Failed to send any text messages');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send text messages. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingSMS(false);
    }
  };

  const handleSMSAndPrint = async () => {
    await handleSMS();
    handlePrint();
  };

  const handleAssignMore = () => {
    // Reset all state
    setCurrentStep(1);
    setSelectedPatient(null);
    setSelectedDentist(null);
    setSelectedMedCards([]);
    setAssignedCardIds([]);
    setTempEditedContent({});
    setPatientSearchTerm('');
    setDentistSearchTerm('');
    setNotes('');
    
    toast({
      title: "Ready",
      description: "Ready to assign more Med Cards",
    });
  };

  // Helper function to get card content (with temporary edits or original)
  const getCardContent = (cardId) => {
    const card = medCards.find(c => c.id === cardId);
    return tempEditedContent[cardId] || card?.customContent || card?.baseContent || '';
  };

  // Helper function to format card content for display
  const formatCardContent = (content) => {
    if (!content) return [];
    
    // Split by double newlines for sections
    const sections = content.split(/\n\n+/).filter(line => line.trim());
    return sections;
  };

  // Filter out Provider Notice from patient-facing content
  const filterProviderNotice = (content) => {
    if (!content) return '';
    const lines = content.split('\n');
    const filtered = lines.filter(line => {
      const lower = line.toLowerCase();
      return !lower.includes('provider notice') && 
             !lower.includes('review this card') && 
             !lower.includes('confirm accuracy');
    });
    return filtered.join('\n');
  };

  // Navigation helpers
  const canGoNext = () => {
    if (currentStep === 1) return selectedPatient !== null;
    if (currentStep === 2) return selectedDentist !== null;
    if (currentStep === 3) return selectedMedCards.length > 0;
    return false;
  };

  const handleNext = () => {
    if (canGoNext()) {
      if (currentStep === 3) {
        handleAssignCards();
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Group Med Cards by category
  const groupedMedCards = medCards.reduce((acc, card) => {
    const category = card.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(card);
    return acc;
  }, {});

  if (checkingDisclaimer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print Styles */}
      <style>{printStyles}</style>

      {/* Disclaimer Modal */}
      <Dialog open={showDisclaimer} onOpenChange={() => {}}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-yellow-600" />
              Med Cards Provider Disclaimer
            </DialogTitle>
            <DialogDescription>
              Please review and accept the disclaimer to use Med Cards
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg space-y-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Purpose & Scope</h4>
                <p>DAN Med Cards are patient-education templates to support clinician-patient communication. They are not medical advice, treatment directives, or a substitute for professional judgment.</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Professional Review Required</h4>
                <p>Before distribution, the prescribing dentist must review and, as needed, edit each handout to confirm the correct drug, strength, route, adult/pediatric dosing, timing/duration, and patient-specific adjustments.</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Not a Prescription</h4>
                <p>These materials are not a prescription, medication order, or authorization to dispense.</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Provider Responsibility</h4>
                <p>You are solely responsible for verifying accuracy and for all clinical decisions.</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Digital Signature</label>
              <Input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Type your full name to accept"
              />
            </div>

            <Button 
              onClick={handleAcceptDisclaimer} 
              disabled={loading || !signature.trim()}
              className="w-full"
            >
              {loading ? 'Accepting...' : 'Accept & Continue'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Edit Med Card Content</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetToOriginal}
                className="flex items-center space-x-1"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Reset to Original</span>
              </Button>
            </DialogTitle>
            <DialogDescription>
              Edit the Med Card content for this assignment only (changes won't affect your library)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm">
              <p className="text-yellow-800">
                <strong>Note:</strong> Changes made here are temporary and only apply to this assignment. 
                Your library will remain unchanged.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Full Text Content</label>
              <textarea
                id="edit-textarea"
                defaultValue={editingCard ? (tempEditedContent[editingCard.id] || editingCard.customContent || editingCard.baseContent || '') : ''}
                className="w-full h-96 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                placeholder="Enter full text content..."
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingCard(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Return to Dashboard</span>
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Assign Med Card</h1>
                <p className="text-gray-600">Assign medication instructions to patient</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 flex-1">
              {[1, 2, 3, 4].map((stepNum) => (
                <React.Fragment key={stepNum}>
                  <div className="flex items-center">
                    <div className={`
                      flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold
                      ${currentStep === stepNum ? 'bg-blue-600 text-white' : 
                        currentStep > stepNum ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}
                    `}>
                      {currentStep > stepNum ? <Check className="h-4 w-4" /> : stepNum}
                    </div>
                    <span className={`ml-2 text-sm font-medium ${
                      currentStep === stepNum ? 'text-blue-600' : 
                      currentStep > stepNum ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {stepNum === 1 && 'Patient'}
                      {stepNum === 2 && 'Dentist'}
                      {stepNum === 3 && 'Med Cards'}
                      {stepNum === 4 && 'Delivery'}
                    </span>
                  </div>
                  {stepNum < 4 && (
                    <ChevronRight className={`h-4 w-4 mx-2 ${
                      currentStep > stepNum ? 'text-green-600' : 'text-gray-400'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Step 1: Select Patient */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Step 1: Select a Patient</CardTitle>
              <p className="text-sm text-gray-600">Choose the patient who will receive the Med Card</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Search box - only show when no patient selected */}
                {!selectedPatient && (
                  <input
                    type="text"
                    placeholder="Type patient name to search..."
                    value={patientSearchTerm}
                    onChange={(e) => setPatientSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
                
                {/* Selected Patient Display */}
                {selectedPatient && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-900">
                        {selectedPatient.firstName} {selectedPatient.lastName}
                      </p>
                      <p className="text-sm text-blue-700">{selectedPatient.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedPatient(null);
                        setPatientSearchTerm('');
                      }}
                      className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                    >
                      Change Patient
                    </button>
                  </div>
                )}
                
                {/* Clickable Patient List */}
                {patientSearchTerm && !selectedPatient && (
                  <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                    {filteredPatients.length > 0 ? (
                      filteredPatients.map((patient) => {
                        // Check for duplicate names
                        const duplicates = filteredPatients.filter(p => 
                          p.firstName === patient.firstName && p.lastName === patient.lastName
                        );
                        const showEmail = duplicates.length > 1;
                        
                        return (
                          <div
                            key={patient.id}
                            onClick={() => {
                              setSelectedPatient(patient);
                              setPatientSearchTerm('');
                            }}
                            className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                          >
                            <p className="font-medium text-gray-900">
                              {patient.firstName} {patient.lastName}
                            </p>
                            {showEmail && patient.email && (
                              <p className="text-sm text-gray-600">{patient.email}</p>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-3 text-center text-gray-500">
                        No patients match "{patientSearchTerm}"
                      </div>
                    )}
                  </div>
                )}
                
                {patientSearchTerm && filteredPatients.length > 0 && !selectedPatient && (
                  <p className="text-xs text-gray-600">
                    {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''} found - click to select
                  </p>
                )}

                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={handleNext}
                    disabled={!canGoNext()}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Dentist */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Step 2: Select A Dentist</CardTitle>
              <p className="text-sm text-gray-600">Choose the dentist responsible for this Med Card</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={dentistSearchTerm}
                    onChange={(e) => setDentistSearchTerm(e.target.value)}
                    placeholder="Search dentists by name or email..."
                    className="pl-10"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto">
                  {filteredDentists.map((dentist) => (
                    <div
                      key={dentist.id}
                      onClick={() => setSelectedDentist(dentist)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                        selectedDentist?.id === dentist.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-full ${
                          selectedDentist?.id === dentist.id ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <User className="h-5 w-5 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{dentist.firstName} {dentist.lastName}</p>
                          <p className="text-xs text-gray-600 mt-1">{dentist.email}</p>
                          {selectedDentist?.id === dentist.id && (
                            <div className="mt-2">
                              <Check className="h-5 w-5 text-blue-600" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={!canGoNext()}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Select & Edit Med Cards */}
        {currentStep === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center justify-between">
                <span>Step 3: Select One or More Med Cards</span>
                <Badge variant="default" className="text-base px-4 py-1">
                  {selectedMedCards.length} Selected
                </Badge>
              </CardTitle>
              <p className="text-sm text-gray-600">Check the boxes to select Med Cards, then edit if needed</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Patient and Dentist Summary */}
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Patient</p>
                      <p className="font-semibold">{selectedPatient?.firstName} {selectedPatient?.lastName}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Dentist</p>
                      <p className="font-semibold">{selectedDentist?.firstName} {selectedDentist?.lastName}</p>
                    </div>
                  </div>
                </div>

                {/* Med Cards List with Checkboxes */}
                <div className="max-h-[500px] overflow-y-auto border border-gray-200 rounded-lg">
                  {Object.entries(groupedMedCards).map(([category, cards]) => (
                    <div key={category} className="border-b border-gray-200 last:border-b-0">
                      <div className="bg-gray-50 px-4 py-2 sticky top-0 z-10">
                        <h3 className="font-semibold text-sm text-gray-700">{category}</h3>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {cards.map((card) => {
                          const isSelected = selectedMedCards.includes(card.id);
                          const hasEdit = tempEditedContent[card.id];
                          
                          return (
                            <div key={card.id} className={`p-3 hover:bg-gray-50 transition ${isSelected ? 'bg-blue-50' : ''}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3 flex-1">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleMedCard(card.id)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                  <div className="flex items-center space-x-2 flex-1">
                                    <FileText className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm font-medium">{card.name}</span>
                                    {hasEdit && (
                                      <Badge variant="secondary" className="text-xs">Edited</Badge>
                                    )}
                                  </div>
                                </div>
                                {isSelected && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenEditModal(card)}
                                  >
                                    <Edit className="h-3 w-3 mr-1" />
                                    Edit
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={!canGoNext() || loading}
                  >
                    {loading ? 'Assigning...' : `Assign ${selectedMedCards.length} Med Card(s) to Patient`}
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Print, Email or Both */}
        {currentStep === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center text-green-600">
                <Check className="h-6 w-6 mr-2" />
                Step 4: Med Cards Assigned Successfully!
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Assignment Summary */}
                <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
                  <h3 className="font-semibold text-lg mb-4">Assignment Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Patient</p>
                      <p className="font-semibold">{selectedPatient?.firstName} {selectedPatient?.lastName}</p>
                      <p className="text-gray-600 text-xs">{selectedPatient?.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Dentist</p>
                      <p className="font-semibold">{selectedDentist?.firstName} {selectedDentist?.lastName}</p>
                      <p className="text-gray-600 text-xs">{selectedDentist?.email}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-gray-600 mb-2">Assigned Med Cards ({assignedCardIds.length})</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {assignedCardIds.map(cardId => {
                        const card = medCards.find(c => c.id === cardId);
                        return card ? (
                          <div key={cardId} className="text-xs bg-white px-3 py-2 rounded border border-green-200">
                            {card.name}
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>

                {/* Delivery Options */}
                <div>
                  <h3 className="font-semibold text-lg mb-4">Choose Delivery Method</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Button
                      onClick={handlePrint}
                      disabled={loading || sendingSMS}
                      size="lg"
                      variant="outline"
                      className="h-24 flex flex-col items-center justify-center space-y-2"
                    >
                      <Printer className="h-8 w-8" />
                      <span>Print Only</span>
                    </Button>
                    
                    <Button
                      onClick={handleEmail}
                      disabled={loading || sendingSMS}
                      size="lg"
                      variant="outline"
                      className="h-24 flex flex-col items-center justify-center space-y-2"
                    >
                      <Mail className="h-8 w-8" />
                      <span>Email Only</span>
                    </Button>
                    
                    <Button
                      onClick={handleSMS}
                      disabled={loading || sendingSMS || !(selectedPatient?.cellPhone || selectedPatient?.phone)}
                      size="lg"
                      variant="outline"
                      className="h-24 flex flex-col items-center justify-center space-y-2 text-green-600 hover:text-green-700 hover:border-green-300"
                    >
                      <MessageSquare className="h-8 w-8" />
                      <span>{sendingSMS ? 'Sending...' : 'Text Only'}</span>
                      {!(selectedPatient?.cellPhone || selectedPatient?.phone) && (
                        <span className="text-xs text-red-500">No phone #</span>
                      )}
                    </Button>
                    
                    <Button
                      onClick={handleBoth}
                      disabled={loading || sendingSMS}
                      size="lg"
                      className="h-24 flex flex-col items-center justify-center space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <Printer className="h-6 w-6" />
                        <Mail className="h-6 w-6" />
                      </div>
                      <span>Print & Email</span>
                    </Button>
                  </div>
                  
                  {/* Second row for combined options */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Button
                      onClick={handleSMSAndPrint}
                      disabled={loading || sendingSMS || !(selectedPatient?.cellPhone || selectedPatient?.phone)}
                      size="lg"
                      variant="outline"
                      className="h-20 flex flex-col items-center justify-center space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="h-6 w-6 text-green-600" />
                        <Printer className="h-6 w-6" />
                      </div>
                      <span>Text & Print</span>
                    </Button>
                    
                    <Button
                      onClick={async () => {
                        await handleEmail();
                        await handleSMS();
                        handlePrint();
                      }}
                      disabled={loading || sendingSMS}
                      size="lg"
                      className="h-20 flex flex-col items-center justify-center space-y-2 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
                    >
                      <div className="flex items-center space-x-2">
                        <Printer className="h-5 w-5" />
                        <Mail className="h-5 w-5" />
                        <MessageSquare className="h-5 w-5" />
                      </div>
                      <span>All: Print, Email & Text</span>
                    </Button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={handleAssignMore}
                  >
                    Assign More Med Cards
                  </Button>
                  <Button
                    onClick={() => navigate('/med-cards/library')}
                  >
                    Go to Med Card Library
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default AssignMedCardPage;
